import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import { useIntl } from "react-intl";

import { getPlan, getPlans, getRole, getUsageLimits } from "@app/store/iam";
import { getUsersOfEntireOrganization } from "@app/store/members";
import showRecordLimitAlertModal from "@app/util/limitAlerts/showRecordLimitAlertModal";

export const useRecordLimitCheck = (isImport?: boolean) => {
  const intl = useIntl();
  const usageLimits = useSelector(getUsageLimits)!;
  const currentPlan = useSelector(getPlan)!;
  const plans = useSelector(getPlans);
  const currentRole = useSelector(getRole)!;
  const users = useSelector(getUsersOfEntireOrganization);

  return useCallback(
    (callback: () => void) => {
      if (
        usageLimits.records.limit === -1 ||
        usageLimits.records.limit > (usageLimits.records.usage ?? 0)
      ) {
        callback();
      } else {
        showRecordLimitAlertModal(intl, {
          currentPlan,
          currentRole,
          isImport,
          plans,
          usageLimits,
          users,
        });
      }
    },
    [usageLimits, intl, currentPlan, currentRole, isImport, plans, users]
  );
};

export const useRecordLimitCheckWith = (callback: () => void) => {
  const intl = useIntl();
  const usageLimits = useSelector(getUsageLimits)!;
  const currentPlan = useSelector(getPlan)!;
  const plans = useSelector(getPlans);
  const currentRole = useSelector(getRole)!;
  const users = useSelector(getUsersOfEntireOrganization);

  return useCallback(() => {
    if (
      usageLimits.records.limit === -1 ||
      usageLimits.records.limit > (usageLimits.records.usage ?? 0)
    ) {
      callback();
    } else {
      showRecordLimitAlertModal(intl, { currentPlan, currentRole, plans, usageLimits, users });
    }
  }, [intl, currentPlan, usageLimits, plans, callback, currentRole, users]);
};

export const useRecordLimitAlert = (active: boolean, onHide?: () => void, isImport?: boolean) => {
  const intl = useIntl();
  const usageLimits = useSelector(getUsageLimits)!;
  const currentPlan = useSelector(getPlan)!;
  const plans = useSelector(getPlans);
  const currentRole = useSelector(getRole)!;
  const users = useSelector(getUsersOfEntireOrganization);

  useEffect(() => {
    if (!active) {
      return;
    }
    const result = showRecordLimitAlertModal(intl, {
      currentPlan,
      currentRole,
      isImport,
      onHide,
      plans,
      usageLimits,
      users,
    });
    return () => result?.destroy();
  }, [intl, currentPlan, currentRole, isImport, plans, usageLimits, users, active, onHide]);
};

export const useIsOutOfRecordLimit = () => {
  const usageLimits = useSelector(getUsageLimits)!;
  return (
    usageLimits.records.limit >= 0 && usageLimits.records.limit <= (usageLimits.records.usage ?? 0)
  );
};
